import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MapPin } from "lucide-react";
import {
  AssetLocationView,
  CreateAssetLocationInput,
  UpdateAssetLocationInput,
  getAssetLocations,
  createAssetLocation,
  updateAssetLocation,
  archiveAssetLocation,
} from "../../../../api/General/assets";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import AlertDialog from "@/components/ui/AlertDialog";
import { Spinner } from "@/components/ui/Spinner";
import { useToast, ToastRenderer } from "@/hooks/useToast";

export default function AssetLocationsTab() {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [locations, setLocations] = useState<AssetLocationView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Modal state
  const [formOpen, setFormOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<AssetLocationView | null>(null);
  const [saving, setSaving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<AssetLocationView | null>(null);
  const [archiving, setArchiving] = useState(false);

  // Form fields
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadLocations = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await getAssetLocations();
      if (result.errors.length === 0) {
        setLocations(result.payload);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const openAdd = () => {
    setEditingLocation(null);
    setName("");
    setAddress("");
    setNotes("");
    setErrors({});
    setFormOpen(true);
  };

  const openEdit = (loc: AssetLocationView) => {
    setEditingLocation(loc);
    setName(loc.name);
    setAddress(loc.address ?? "");
    setNotes(loc.notes ?? "");
    setErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingLocation(null);
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = t("assetLocations.nameRequired");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      if (editingLocation) {
        const input: UpdateAssetLocationInput = {
          id: editingLocation.id,
          name: name.trim(),
          address: address.trim() || undefined,
          notes: notes.trim() || undefined,
        };
        const result = await updateAssetLocation(input);
        if (result.errors.length === 0) {
          showToast(t("assetLocations.locationUpdated"), "success");
          closeForm();
          loadLocations();
        } else {
          showToast(result.errors[0]?.description || t("Something went wrong"), "error");
        }
      } else {
        const input: CreateAssetLocationInput = {
          name: name.trim(),
          address: address.trim() || undefined,
          notes: notes.trim() || undefined,
        };
        const result = await createAssetLocation(input);
        if (result.errors.length === 0) {
          showToast(t("assetLocations.locationCreated"), "success");
          closeForm();
          loadLocations();
        } else {
          showToast(result.errors[0]?.description || t("Something went wrong"), "error");
        }
      }
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setSaving(false);
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      const result = await archiveAssetLocation(archiveTarget.id);
      if (result.errors.length === 0) {
        showToast(t("assetLocations.locationArchived"), "success");
        loadLocations();
      } else {
        showToast(result.errors[0]?.description || t("Something went wrong"), "error");
      }
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setArchiving(false);
      setArchiveTarget(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">{t("assetLocations.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("assetLocations.subtitle")}</p>
        </div>
        <Button
          variant="outline"
          onClick={openAdd}
          data-testid="add-location-btn"
        >
          <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t("assetLocations.addLocation")}
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12" data-testid="asset-locations-error">
          <p className="text-sm text-destructive">{t("assetLocations.loadError")}</p>
        </div>
      ) : locations.length === 0 ? (
        <div className="text-center py-12" data-testid="asset-locations-empty">
          <MapPin className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-2 text-sm font-medium text-foreground">{t("assetLocations.noLocations")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("assetLocations.noLocationsDescription")}</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetLocations.name")}
                </th>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetLocations.address")}
                </th>
                <th className="px-4 py-3 text-end text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetLocations.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {locations.map((loc) => (
                <tr key={loc.id} className="hover:bg-muted/30 transition-colors" data-testid={`location-row-${loc.id}`}>
                  <td className="px-4 py-3 text-sm font-medium text-foreground">{loc.name}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{loc.address || "—"}</td>
                  <td className="px-4 py-3 text-sm text-end">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => openEdit(loc)}
                        data-testid={`edit-location-${loc.id}`}
                      >
                        <svg className="w-3.5 h-3.5 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        {t("common.edit")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => setArchiveTarget(loc)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`archive-location-${loc.id}`}
                      >
                        <svg className="w-3.5 h-3.5 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        {t("assets.archive")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Modal */}
      <Dialog open={formOpen} onOpenChange={(o) => !o && closeForm()}>
        <DialogContent size="default">
          <DialogHeader>
            <DialogTitle>
              {editingLocation ? t("assetLocations.editLocation") : t("assetLocations.addLocation")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <DialogBody className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assetLocations.name")} *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${errors.name ? "border-destructive" : "border-border"}`}
                  data-testid="location-name-input"
                  autoFocus
                />
                {errors.name && <p className="text-destructive text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assetLocations.address")}
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="location-address-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assetLocations.notes")}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                  data-testid="location-notes-input"
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeForm}
                data-testid="location-form-cancel"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                loading={saving}
                data-testid="location-form-save"
              >
                {saving ? t("common.saving") : editingLocation ? t("common.save") : t("assetLocations.addLocation")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation */}
      <AlertDialog
        open={!!archiveTarget}
        onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}
        title={t("assets.archive")}
        description={t("assetLocations.confirmArchive")}
        variant="destructive"
        confirmLabel={t("assets.archive")}
        cancelLabel={t("common.cancel")}
        isLoading={archiving}
        onConfirm={confirmArchive}
      />

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
}
